import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DiagnosticsRepository } from '../../../../infrastructure/http/repositories/diagnostics.repository';
import envConfig from '../../../../env-config';
import type {
  StorageFileItem,
  StorageFilesResult,
  SftpServerGroup,
  SftpFolderGroup,
  SftpSnapshotServer,
  SftpSnapshotFolder,
  ObjectStorageFolder,
} from '../../models/diagnostics.models';

type StorageSection = 'input' | 'output' | 'objectStorage';

interface TreeNode {
  name: string;
  nodePath: string;
  children: TreeNode[];
  file?: StorageFileItem;
}

interface FlatTreeItem {
  name: string;
  nodePath: string;
  depth: number;
  isFolder: boolean;
  isExpanded: boolean;
  file?: StorageFileItem;
}

@Component({
  selector: 'app-diagnostic-file-storage-tab',
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './diagnostic-file-storage-tab.component.html',
  styleUrls: ['./diagnostic-file-storage-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticFileStorageTabComponent implements OnInit {
  private readonly repo = inject(DiagnosticsRepository);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly isSnapshotMode = envConfig.useSftpSnapshot === true;

  readonly PAGE_SIZE = 10; // Files per page
  readonly PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

  storageState: {
    loading: boolean;
    showSection: boolean;
    data: StorageFilesResult | null;
    error: string | null;
    inputLoading: boolean;
    outputLoading: boolean;
    objectStorageLoading: boolean;
  } = {
    loading: false,
    showSection: false,
    data: null,
    error: null,
    inputLoading: false,
    outputLoading: false,
    objectStorageLoading: false,
  };

  // Search state per storage section
  searchTerms = {
    input: '',
    output: '',
    objectStorage: '',
  };

  // Pagination state: per-server for SFTP, per-folder for Object Storage
  pagination: {
    input: Record<string, { currentPage: number; pageSize: number }>;
    output: Record<string, { currentPage: number; pageSize: number }>;
    objectStorage: Record<string, { currentPage: number; pageSize: number }>;
  } = {
    input: {},
    output: {},
    objectStorage: {},
  };

  lastRefreshed: Date | null = null;

  // Tree view state for object storage
  objectStorageTrees: Record<string, TreeNode[]> = {};
  expandedTreeNodes: Record<string, Set<string>> = {};

  // Section expansion state
  expandedSections = {
    input: true,
    objectStorage: true,
    output: true,
  };

  ngOnInit(): void {
    this.loadStorageFiles();
  }

  refresh(): void {
    this.loadStorageFiles();
  }

  loadStorageFiles(): void {
    this.storageState.loading = true;
    this.storageState.showSection = true;
    this.storageState.data = {
      inputSftpServers: [],
      outputSftpServers: [],
      objectStorageFolders: [],
    };
    this.cdr.markForCheck();

    if (this.isSnapshotMode) {
      this.loadSftpSnapshotSection();
    } else {
      this.loadInputSftpSection();
      this.loadOutputSftpSection();
    }
    this.loadObjectStorageSection();
  }

  private loadSftpSnapshotSection(): void {
    this.storageState.inputLoading = true;
    this.storageState.outputLoading = true;
    this.cdr.markForCheck();

    this.repo.getStorageSftpSnapshots().subscribe({
      next: result => {
        this.storageState.inputLoading = false;
        this.storageState.outputLoading = false;

        if (this.storageState.data) {
          this.storageState.data.inputSftpServers = result.inputServers.map(s =>
            this.mapSnapshotServer(s)
          );
          this.storageState.data.outputSftpServers = result.outputServers.map(
            s => this.mapSnapshotServer(s)
          );

          for (const server of this.storageState.data.inputSftpServers) {
            if (!this.pagination.input[server.address]) {
              this.pagination.input[server.address] = {
                currentPage: 1,
                pageSize: 10,
              };
            }
          }
          for (const server of this.storageState.data.outputSftpServers) {
            if (!this.pagination.output[server.address]) {
              this.pagination.output[server.address] = {
                currentPage: 1,
                pageSize: 10,
              };
            }
          }
        }

        this.updateRefreshTimeIfAllLoaded();
        this.cdr.markForCheck();
      },
      error: () => {
        this.storageState.inputLoading = false;
        this.storageState.outputLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private mapSnapshotServer(server: SftpSnapshotServer): SftpServerGroup {
    return {
      hostName: server.hostName,
      address: server.address,
      error: null,
      folders: server.folders.map((f: SftpSnapshotFolder) => ({
        folderPath: f.folderPath,
        folderType: f.folderLabel,
        error: null,
        files: f.files.map(file => ({
          name: file.name,
          fullPath: `${f.folderPath}/${file.name}`,
          sizeBytes: file.sizeBytes ?? 0,
          lastModified: file.lastModifiedAt,
          isDirectory: false,
        })),
      })),
    };
  }

  private loadInputSftpSection(): void {
    this.storageState.inputLoading = true;
    this.cdr.markForCheck();

    this.repo.getStorageSftpInputFiles().subscribe({
      next: result => {
        this.storageState.inputLoading = false;
        if (this.storageState.data) {
          this.storageState.data.inputSftpServers = result.sftpServers || [];
          // Initialize pagination for each server
          for (const server of result.sftpServers || []) {
            if (!this.pagination.input[server.address]) {
              this.pagination.input[server.address] = {
                currentPage: 1,
                pageSize: 10,
              };
            }
          }
        }
        this.updateRefreshTimeIfAllLoaded();
        this.cdr.markForCheck();
      },
      error: () => {
        this.storageState.inputLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadOutputSftpSection(): void {
    this.storageState.outputLoading = true;
    this.cdr.markForCheck();

    this.repo.getStorageSftpOutputFiles().subscribe({
      next: result => {
        this.storageState.outputLoading = false;
        if (this.storageState.data) {
          this.storageState.data.outputSftpServers = result.sftpServers || [];
          // Initialize pagination for each server
          for (const server of result.sftpServers || []) {
            if (!this.pagination.output[server.address]) {
              this.pagination.output[server.address] = {
                currentPage: 1,
                pageSize: 10,
              };
            }
          }
        }
        this.updateRefreshTimeIfAllLoaded();
        this.cdr.markForCheck();
      },
      error: () => {
        this.storageState.outputLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadObjectStorageSection(): void {
    this.storageState.objectStorageLoading = true;
    this.cdr.markForCheck();

    this.repo.getStorageObjectFiles().subscribe({
      next: result => {
        this.storageState.objectStorageLoading = false;
        if (this.storageState.data) {
          this.storageState.data.objectStorageFolders =
            result.objectStorageFolders || [];
          // Build tree for each folder
          for (const folder of result.objectStorageFolders || []) {
            this.objectStorageTrees[folder.folderPath] = this.buildTree(
              folder.files,
              folder.folderPath
            );
            this.expandedTreeNodes[folder.folderPath] = new Set();
          }
        }
        this.updateRefreshTimeIfAllLoaded();
        this.cdr.markForCheck();
      },
      error: () => {
        this.storageState.objectStorageLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private updateRefreshTimeIfAllLoaded(): void {
    // Update refresh time only when all sections are loaded
    if (
      !this.storageState.inputLoading &&
      !this.storageState.outputLoading &&
      !this.storageState.objectStorageLoading
    ) {
      this.storageState.loading = false;
      this.lastRefreshed = new Date();
    }
  }

  toggleSection(section: 'input' | 'objectStorage' | 'output'): void {
    this.expandedSections[section] = !this.expandedSections[section];
    this.cdr.markForCheck();
  }

  formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(2)} ${units[unitIndex]}`;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  }

  getFileTypeFolder(): string {
    return 'Folder';
  }

  getFileTypeFile(): string {
    return 'File';
  }

  getFileType(file: StorageFileItem): string {
    return file.isDirectory ? this.getFileTypeFolder() : this.getFileTypeFile();
  }

  // Helper method to get pagination state for a server/folder
  getPaginationState(
    section: StorageSection,
    key: string
  ): { currentPage: number; pageSize: number } {
    return this.pagination[section][key] || { currentPage: 1, pageSize: 10 };
  }

  // Helper method to get all files from a server (flattened)
  getAllServerFiles(server: SftpServerGroup): StorageFileItem[] {
    return server.folders?.flatMap((f: SftpFolderGroup) => f.files) || [];
  }

  getSftpServerCount(): number {
    return (
      (this.storageState.data?.inputSftpServers.length ?? 0) +
      (this.storageState.data?.outputSftpServers.length ?? 0)
    );
  }

  getObjectStorageFolderCount(): number {
    return this.storageState.data?.objectStorageFolders.length ?? 0;
  }

  getTotalFiles(
    items: {
      files: { isDirectory: boolean }[];
    }[]
  ): number {
    return items.reduce((sum, item) => {
      return sum + item.files.filter(f => !f.isDirectory).length;
    }, 0);
  }

  getTotalFolders(
    items: {
      files: { isDirectory: boolean }[];
    }[]
  ): number {
    return items.reduce((sum, item) => {
      return sum + item.files.filter(f => f.isDirectory).length;
    }, 0);
  }

  // Get all files from a section (across all folders)
  getAllFilesInSection(section: StorageSection): {
    name: string;
    fullPath: string;
    sizeBytes: number;
    lastModified: string | null;
    isDirectory: boolean;
  }[] {
    if (!this.storageState.data) return [];

    if (section === 'objectStorage') {
      return this.storageState.data.objectStorageFolders.flatMap(
        (f: ObjectStorageFolder) => f.files
      );
    }

    const servers =
      section === 'input'
        ? this.storageState.data.inputSftpServers
        : this.storageState.data.outputSftpServers;

    return servers.flatMap((server: SftpServerGroup) =>
      server.folders.flatMap((folder: SftpFolderGroup) => folder.files)
    );
  }

  // Filter files by search term (case-insensitive)
  getFilteredFiles(
    files: {
      name: string;
      fullPath: string;
      sizeBytes: number;
      lastModified: string | null;
      isDirectory: boolean;
    }[],
    searchTerm: string
  ): {
    name: string;
    fullPath: string;
    sizeBytes: number;
    lastModified: string | null;
    isDirectory: boolean;
  }[] {
    if (!searchTerm.trim()) {
      return files;
    }
    const lowerTerm = searchTerm.toLowerCase();
    return files.filter(file => file.name.toLowerCase().includes(lowerTerm));
  }

  // Get displayed files for current page and search term
  getDisplayedFiles(
    files: {
      name: string;
      fullPath: string;
      sizeBytes: number;
      lastModified: string | null;
      isDirectory: boolean;
    }[],
    section: StorageSection,
    key: string,
    searchTerm: string
  ): {
    name: string;
    fullPath: string;
    sizeBytes: number;
    lastModified: string | null;
    isDirectory: boolean;
  }[] {
    const filtered = this.getFilteredFiles(files, searchTerm);
    const paginationState = this.pagination[section][key];
    if (!paginationState) return [];
    const startIndex =
      (paginationState.currentPage - 1) * paginationState.pageSize;
    const endIndex = startIndex + paginationState.pageSize;
    return filtered.slice(startIndex, endIndex);
  }

  // Get total pages for a section/server/folder
  getTotalPages(
    section: StorageSection,
    key: string,
    files: StorageFileItem[]
  ): number {
    const paginationState = this.pagination[section][key];
    if (!paginationState) return 0;
    const filtered = this.getFilteredFiles(files, this.searchTerms[section]);
    return Math.ceil(filtered.length / paginationState.pageSize);
  }

  getTotalPagesForTree(
    section: StorageSection,
    key: string,
    flatTreeItems: FlatTreeItem[]
  ): number {
    const paginationState = this.pagination[section][key];
    if (!paginationState) return 1;
    return Math.max(
      1,
      Math.ceil(flatTreeItems.length / paginationState.pageSize)
    );
  }

  // Check if there are more pages
  hasNextPage(
    section: StorageSection,
    key: string,
    files: StorageFileItem[]
  ): boolean {
    const paginationState = this.pagination[section][key];
    if (!paginationState) return false;
    const totalPages = this.getTotalPages(section, key, files);
    return paginationState.currentPage < totalPages;
  }

  // Check if there is a previous page
  hasPreviousPage(section: StorageSection, key: string): boolean {
    // Ensure pagination state is initialized
    if (!this.pagination[section][key]) {
      this.pagination[section][key] = { currentPage: 1, pageSize: 10 };
    }
    const paginationState = this.pagination[section][key];
    return paginationState.currentPage > 1;
  }

  // Navigation methods
  goToFirstPage(section: StorageSection, key: string): void {
    const paginationState = this.pagination[section][key];
    if (paginationState) {
      paginationState.currentPage = 1;
      this.cdr.markForCheck();
    }
  }

  goToPreviousPage(section: StorageSection, key: string): void {
    if (this.hasPreviousPage(section, key)) {
      this.pagination[section][key].currentPage--;
      this.cdr.markForCheck();
    }
  }

  goToNextPage(
    section: StorageSection,
    key: string,
    files: StorageFileItem[]
  ): void {
    if (this.hasNextPage(section, key, files)) {
      this.pagination[section][key].currentPage++;
      this.cdr.markForCheck();
    }
  }

  goToLastPage(
    section: StorageSection,
    key: string,
    files: StorageFileItem[]
  ): void {
    const totalPages = this.getTotalPages(section, key, files);
    const paginationState = this.pagination[section][key];
    if (paginationState) {
      paginationState.currentPage = totalPages;
      this.cdr.markForCheck();
    }
  }

  hasNextPageForTree(
    section: StorageSection,
    key: string,
    flatTreeItems: FlatTreeItem[]
  ): boolean {
    // Ensure pagination state is initialized
    if (!this.pagination[section][key]) {
      this.pagination[section][key] = { currentPage: 1, pageSize: 10 };
    }
    const paginationState = this.pagination[section][key];
    const totalPages = this.getTotalPagesForTree(section, key, flatTreeItems);
    return paginationState.currentPage < totalPages;
  }

  goToNextPageForTree(
    section: StorageSection,
    key: string,
    flatTreeItems: FlatTreeItem[]
  ): void {
    // Ensure pagination state is initialized
    if (!this.pagination[section][key]) {
      this.pagination[section][key] = { currentPage: 1, pageSize: 10 };
    }
    if (this.hasNextPageForTree(section, key, flatTreeItems)) {
      this.pagination[section][key].currentPage++;
      this.cdr.markForCheck();
    }
  }

  goToLastPageForTree(
    section: StorageSection,
    key: string,
    flatTreeItems: FlatTreeItem[]
  ): void {
    // Ensure pagination state is initialized
    if (!this.pagination[section][key]) {
      this.pagination[section][key] = { currentPage: 1, pageSize: 10 };
    }
    const paginationState = this.pagination[section][key];
    const totalPages = this.getTotalPagesForTree(section, key, flatTreeItems);
    paginationState.currentPage = totalPages;
    this.cdr.markForCheck();
  }

  // Update page size and reset to first page
  setPageSize(section: StorageSection, key: string, pageSize: number): void {
    const paginationState = this.pagination[section][key];
    if (paginationState) {
      paginationState.pageSize = pageSize;
      paginationState.currentPage = 1;
      this.cdr.markForCheck();
    }
  }

  // Update search term and reset to first page for all servers/folders in section
  onSearchChange(section: StorageSection, searchTerm: string): void {
    this.searchTerms[section] = searchTerm;
    // Reset current page for all servers/folders in this section
    for (const key in this.pagination[section]) {
      this.pagination[section][key]['currentPage'] = 1;
    }
    this.cdr.markForCheck();
  }

  getSearchTerm(section: string): string {
    return this.searchTerms[section as StorageSection] || '';
  }

  private buildTree(files: StorageFileItem[], folderPath: string): TreeNode[] {
    const root: TreeNode = {
      name: '',
      nodePath: '',
      children: [],
    };

    for (const file of files) {
      // Get path relative to folderPath
      const relativePath = file.fullPath.startsWith(folderPath + '/')
        ? file.fullPath.slice(folderPath.length + 1)
        : file.fullPath;

      const segments = relativePath.split('/');
      let current = root;

      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const isLast = i === segments.length - 1;
        const pathSoFar = segments.slice(0, i + 1).join('/');

        let child = current.children.find(c => c.name === segment);
        if (!child) {
          child = {
            name: segment,
            nodePath: pathSoFar,
            children: [],
            file: isLast ? file : undefined,
          };
          current.children.push(child);
        }
        current = child;
      }
    }

    return root.children;
  }

  toggleTreeNode(folderPath: string, nodePath: string): void {
    if (!this.expandedTreeNodes[folderPath]) {
      this.expandedTreeNodes[folderPath] = new Set();
    }
    const set = this.expandedTreeNodes[folderPath];
    if (set.has(nodePath)) {
      set.delete(nodePath);
    } else {
      set.add(nodePath);
    }
    this.cdr.markForCheck();
  }

  isTreeNodeExpanded(folderPath: string, nodePath: string): boolean {
    return this.expandedTreeNodes[folderPath]?.has(nodePath) ?? false;
  }

  getFlatTree(folderPath: string, searchTerm: string): FlatTreeItem[] {
    const nodes = this.objectStorageTrees[folderPath] ?? [];
    const result: FlatTreeItem[] = [];
    const matchingNodePaths = new Set<string>();

    // If search term exists, find matching files and their ancestors
    if (searchTerm.trim()) {
      this.findMatchingPaths(
        nodes,
        searchTerm.toLowerCase(),
        matchingNodePaths
      );
    }

    this.flattenTree(
      nodes,
      0,
      folderPath,
      result,
      searchTerm.toLowerCase(),
      matchingNodePaths
    );
    return result;
  }

  getPaginatedFlatTree(folderPath: string, searchTerm: string): FlatTreeItem[] {
    const flatTree = this.getFlatTree(folderPath, searchTerm);
    const paginationKey = folderPath;
    const pagination = this.getPaginationState('objectStorage', paginationKey);
    const pageSize = pagination.pageSize;
    const currentPage = pagination.currentPage;

    // Count only parent-level items (depth 0) for pagination
    const parentItems = flatTree.filter(item => item.depth === 0);
    const startParentIndex = (currentPage - 1) * pageSize;
    const endParentIndex = startParentIndex + pageSize;
    const visibleParents = parentItems.slice(startParentIndex, endParentIndex);

    // Build result including all children of visible parents
    const result: FlatTreeItem[] = [];
    for (const parent of visibleParents) {
      const parentIndexInTree = flatTree.indexOf(parent);
      result.push(parent);

      // Add all children of this parent
      for (let i = parentIndexInTree + 1; i < flatTree.length; i++) {
        const item = flatTree[i];
        if (item.depth <= 0) break; // Stop at next parent
        result.push(item);
      }
    }

    return result;
  }

  private findMatchingPaths(
    nodes: TreeNode[],
    searchTerm: string,
    matching: Set<string>
  ): void {
    for (const node of nodes) {
      if (node.file && node.name.toLowerCase().includes(searchTerm)) {
        // Mark this file and all ancestors
        matching.add(node.nodePath);
        this.markAncestors(node.nodePath, matching);
      }
      this.findMatchingPaths(node.children, searchTerm, matching);
    }
  }

  private markAncestors(nodePath: string, matching: Set<string>): void {
    const segments = nodePath.split('/');
    for (let i = segments.length - 1; i > 0; i--) {
      const ancestorPath = segments.slice(0, i).join('/');
      matching.add(ancestorPath);
    }
  }

  private flattenTree(
    nodes: TreeNode[],
    depth: number,
    folderPath: string,
    result: FlatTreeItem[],
    searchTerm: string,
    matchingPaths: Set<string>
  ): void {
    // Sort nodes: folders by name (alphabetical), files by lastModified (newest first)
    const sortedNodes = [...nodes].sort((a, b) => {
      const aIsFile = !!a.file;
      const bIsFile = !!b.file;

      if (aIsFile && bIsFile) {
        // Both files: sort by lastModified (newest first)
        const aDate = a.file!.lastModified
          ? new Date(a.file!.lastModified).getTime()
          : 0;
        const bDate = b.file!.lastModified
          ? new Date(b.file!.lastModified).getTime()
          : 0;
        return bDate - aDate;
      }

      if (!aIsFile && !bIsFile) {
        // Both folders: sort by name (alphabetical)
        return a.name.localeCompare(b.name);
      }

      // Folders before files
      return aIsFile ? 1 : -1;
    });

    for (const node of sortedNodes) {
      const isFile = !!node.file;
      const isFolder = !isFile && node.children.length > 0;

      // If search term, skip non-matching nodes (unless they're ancestors of matches)
      if (searchTerm && !matchingPaths.has(node.nodePath)) {
        continue;
      }

      // If search term and this is an ancestor of matches, auto-expand
      if (searchTerm && isFolder && matchingPaths.has(node.nodePath)) {
        this.expandedTreeNodes[folderPath]?.add(node.nodePath);
      }

      // Recalculate isExpanded after potential auto-expand
      const isExpanded = this.isTreeNodeExpanded(folderPath, node.nodePath);

      result.push({
        name: node.name,
        nodePath: node.nodePath,
        depth,
        isFolder,
        isExpanded,
        file: node.file,
      });

      // Recurse into children if folder is expanded
      if (isFolder && isExpanded) {
        this.flattenTree(
          node.children,
          depth + 1,
          folderPath,
          result,
          searchTerm,
          matchingPaths
        );
      }
    }
  }
}
